/*
 * ra-core.js — документ /risk-assessment-generator без интерфейса:
 * матрица риска формата, черновик из библиотеки, версия для бригады,
 * печатная вёрстка (PDF и «Печать») и блоки Word (DocxWriter).
 *
 * Вероятность и тяжесть в документе всегда хранятся по шкале 1–5. Матрица —
 * это только то, КАК они показываются: у Германии три ступени, у Франции
 * G × F 1–4, у Испании метод INSST, у Кореи 상/중/하. Поэтому смена формата
 * никогда не теряет оценку, которую человек уже поставил.
 *
 * Работает и в браузере, и под node (tests/js/risk-assessment-core.test.mjs).
 * ES2018-safe, как весь public/js.
 */
(function (root) {
    'use strict';

    var LEVELS = ['E', 'S', 'N', 'A', 'P'];
    // Доли ширины граф — одни на PDF и Word, чтобы документ выглядел одинаково.
    var COL_WEIGHT = { step: 1.3, hazard: 1.7, who: 1.1, risk_before: 0.9, controls: 3.2, risk_after: 0.9, responsible: 1.0, due: 0.9 };
    var RTL = { ar: 1, ur: 1, fa: 1, he: 1 };

    /* Направление текста по первой сильной букве — как dir="auto", но своё:
       html2canvas копирует документ, а Word и вовсе не знает auto, поэтому
       ответ должен быть готов до вёрстки. Повод — 22.10.2026: английская мера
       в арабском документе печаталась с точкой не с того края. Классы символов
       собираются из кодов: в исходнике нет ни одного невидимого символа. */
    function span(a, b) { return String.fromCharCode(a) + '-' + String.fromCharCode(b); }
    var RTL_CH = new RegExp('[' + span(0x0590, 0x08FF) + span(0xFB1D, 0xFDFF) + span(0xFE70, 0xFEFC) + ']');
    var LTR_CH = new RegExp('[A-Za-z' + span(0x00C0, 0x024F) + span(0x0370, 0x058F) + span(0x0900, 0x0DFF)
        + span(0x0E00, 0x0EFF) + span(0x10A0, 0x10FF) + span(0x1100, 0x11FF) + span(0x1E00, 0x1FFF)
        + span(0x3040, 0x30FF) + span(0x3400, 0x9FFF) + span(0xAC00, 0xD7AF) + ']');
    function textDir(s, fallback) {
        s = String(s == null ? '' : s);
        for (var i = 0; i < s.length; i++) {
            var c = s.charAt(i);
            if (RTL_CH.test(c)) return 'rtl';
            if (LTR_CH.test(c)) return 'ltr';
        }
        return fallback || 'ltr';
    }

    /* Текст, чьё направление противоположно направлению отчёта. Дверь PDF
       (report-pdf.js, rtlTextDrawing) рисует КАЖДЫЙ кусок холста в
       направлении отчёта, и у такого текста ломалось трижды (22.10.2026,
       замер на арабском SWMS с английскими мерами):
         · знак у края куска уходил на другую сторону — «gloves.» → «.gloves»,
           «5349–1» → «1–5349»;
         · кусок, перенесённый по дефису, терял свой знак — «a full-body» →
           «a-full body»;
         · хвостовой пробел куска уходил на другой край и сдвигал слово —
           «check itdaily», «ACME PtyLtd».
       Поэтому (marked) метка своего направления (LRM, RLM) стоит в начале
       слова и после КАЖДОГО знака, не являющегося буквой этого направления:
       любой кусок, где бы html2canvas его ни отрезал, держит порядок; и
       (wordSpans) каждое слово — свой элемент, а пробелы — отдельные
       текстовые узлы: у рисуемого куска нет хвостового пробела. Раскладку
       страницы это не меняет. Word меток не получает — там абзацы с
       настоящим направлением (docxBlocks). */
    var LRM = String.fromCharCode(0x200E), RLM = String.fromCharCode(0x200F);
    function markWord(w, d) {
        var m = d === 'rtl' ? RLM : LRM, strong = d === 'rtl' ? RTL_CH : LTR_CH, out = m;
        for (var i = 0; i < w.length; i++) {
            var c = w.charAt(i);
            out += c;
            if (!strong.test(c)) out += m;
        }
        return out + (strong.test(w.charAt(w.length - 1)) ? m : '');
    }
    function marked(s, base) {
        s = String(s == null ? '' : s);
        var d = textDir(s, base);
        if (d === base) return s;
        return s.replace(/\S+/g, function (w) { return markWord(w, d); });
    }
    /**
     * HTML: противоположного направления — словами в span с метками; своего —
     * как есть, а в отчёте справа налево (`always`) тоже словами в span, без
     * меток: у последнего слова строки висящий пробел не входит в его
     * прямоугольник, а рисуется — арабские слова на конце строки склеивались
     * («العاملونفي», тот же замер 22.10.2026).
     */
    function wordSpans(s, base, always) {
        s = String(s == null ? '' : s);
        var d = textDir(s, base);
        if (d === base && !always) return esc(s);
        return s.split(/(\s+)/).map(function (part) {
            if (part === '') return '';
            if (/^\s+$/.test(part)) return esc(part);
            return '<span>' + esc(d === base ? part : markWord(part, d)) + '</span>';
        }).join('');
    }

    function esc(s) {
        return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
            return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
        });
    }
    function clamp5(v) { v = Math.round(Number(v) || 0); return v < 1 ? 1 : (v > 5 ? 5 : v); }
    // 1–5 → три ступени: 1–2 → 1, 3 → 2, 4–5 → 3.
    function to3(v) { v = clamp5(v); return v <= 2 ? 1 : (v === 3 ? 2 : 3); }
    // 1–5 → четыре ступени: 4 и 5 сливаются в «очень».
    function to4(v) { v = clamp5(v); return v >= 4 ? 4 : v; }

    /* Метод INSST: P baja/media/alta × C ligeramente dañino / dañino /
       extremadamente dañino. Родные названия — константы метода. */
    var INSST = {
        p: ['Baja', 'Media', 'Alta'],
        c: ['Ligeramente dañino', 'Dañino', 'Extremadamente dañino'],
        // [p][c] → уровень риска
        grid: [['Trivial', 'Tolerable', 'Moderado'], ['Tolerable', 'Moderado', 'Importante'], ['Moderado', 'Importante', 'Intolerable']],
        band: { Trivial: 'low', Tolerable: 'low', Moderado: 'medium', Importante: 'high', Intolerable: 'extreme' },
    };
    // 위험성 수준 3단계 판단법.
    var KR3 = { low: '하', medium: '중', high: '상' };
    // Gravité / fréquence (INRS ED 840 — échelles à quatre niveaux).
    var FR4 = {
        g: ['Faible', 'Moyenne', 'Grave', 'Très grave'],
        f: ['Rare', 'Occasionnelle', 'Fréquente', 'Très fréquente'],
    };

    /* Шкала, которую показывают селекторы формата: значения по 1–5 и подписи. */
    function scale(matrix, kind, docLang, vocab) {
        var d = (vocab && vocab.doc) || {};
        var i, out = [];
        if (matrix === '3x3' || matrix === 'insst') {
            var native = matrix === 'insst' && docLang === 'es';
            var names = native ? (kind === 'l' ? INSST.p : INSST.c)
                : [1, 2, 3].map(function (n) { return ((kind === 'l' ? d.like3 : d.sev3) || {})[n] || String(n); });
            [1, 3, 5].forEach(function (v, k) { out.push({ v: v, label: names[k] }); });
            return out;
        }
        if (matrix === '4x4') {
            var fr = docLang === 'fr';
            [1, 2, 3, 5].forEach(function (v, k) {
                out.push({ v: v, label: (k + 1) + (fr ? ' — ' + (kind === 'l' ? FR4.f : FR4.g)[k] : '') });
            });
            return out;
        }
        for (i = 1; i <= 5; i++) {
            out.push({ v: i, label: i + ' — ' + (((kind === 'l' ? d.like : d.sev) || {})[i] || '') });
        }
        return out;
    }

    /* Ближайшее значение шкалы — селектор показывает то, что лежит в 1–5. */
    function snap(matrix, kind, v) {
        var opts = scale(matrix, kind, '', null);
        var best = opts[0].v, dist = 99;
        for (var i = 0; i < opts.length; i++) {
            var dd = Math.abs(opts[i].v - clamp5(v));
            if (dd < dist) { dist = dd; best = opts[i].v; }
        }
        return best;
    }

    /**
     * Оценка риска для показа: {text, band}. band — low/medium/high/extreme,
     * по нему красится ячейка; text — то, что печатается.
     */
    function rate(matrix, l, s, docLang, vocab) {
        var risk = ((vocab && vocab.doc) || {}).risk || {};
        l = clamp5(l); s = clamp5(s);
        if (matrix === 'none') return { text: '', band: '' };
        if (matrix === 'insst' && docLang === 'es') {
            var lvl = INSST.grid[to3(l) - 1][to3(s) - 1];
            return { text: lvl, band: INSST.band[lvl] };
        }
        if (matrix === 'kr3' && docLang === 'ko') {
            var sc = l * s, b = sc <= 4 ? 'low' : (sc <= 9 ? 'medium' : 'high');
            return { text: KR3[b], band: b };
        }
        if (matrix === '4x4') {
            var g = to4(s), f = to4(l), p = g * f, b4 = p <= 4 ? 'low' : (p <= 8 ? 'medium' : 'high');
            return { text: g + '×' + f + ' = ' + p + ' — ' + (risk[b4] || b4), band: b4 };
        }
        if (matrix === '3x3' || matrix === 'insst' || matrix === 'kr3') {
            var q = to3(l) * to3(s), b3 = q <= 2 ? 'low' : (q <= 4 ? 'medium' : 'high');
            return { text: risk[b3] || b3, band: b3 };
        }
        var n = l * s, b5 = n <= 4 ? 'low' : (n <= 9 ? 'medium' : (n <= 14 ? 'high' : 'extreme'));
        return { text: n + ' — ' + (risk[b5] || b5), band: b5 };
    }

    function legend(matrix, docLang, vocab) {
        var d = (vocab && vocab.doc) || {};
        if (matrix === 'none') return '';
        if (matrix === 'insst' && docLang === 'es') {
            return 'Probabilidad (Baja / Media / Alta) × Consecuencias (Ligeramente dañino / Dañino / Extremadamente dañino) → Trivial, Tolerable, Moderado, Importante, Intolerable (método general del INSST).';
        }
        if (matrix === 'kr3' && docLang === 'ko') {
            return '위험성 수준 3단계 판단법: 상 · 중 · 하 (빈도 1–5 × 강도 1–5: 1–4 하, 5–9 중, 10 이상 상).';
        }
        if (matrix === '4x4') return d.legend_4x4 || '';
        if (matrix === '3x3' || matrix === 'insst' || matrix === 'kr3') return d.legend_3x3 || '';
        return d.legend_5x5 || '';
    }

    function uid() {
        return 'r' + Math.random().toString(36).slice(2, 9) + Date.now().toString(36).slice(-3);
    }

    /* Отпечаток строки: перевод бригады привязан к ТЕКСТУ строки, а не к её
       номеру — правка строки делает её перевод устаревшим, перестановка нет. */
    function rowHash(r) {
        var s = String(r.hazard || '') + '\u0001' + (r.controls || []).map(function (c) { return c.t; }).join('\u0002');
        var h = 5381;
        for (var i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
        return (h >>> 0).toString(36);
    }

    /**
     * Черновик из библиотеки: виды работ → строки таблицы. Опасность, общая для
     * двух видов работ (падение с высоты у «высоты» и «кровли»), — одна строка:
     * в документе она описана один раз, а не дважды разными словами.
     */
    function buildFromLibrary(works, structure, vocab) {
        var lib = (vocab && vocab.lib) || {};
        var seen = {}, rows = [], ppe = {}, permits = {};
        var list = (works || []).slice();
        if (list.indexOf('general') < 0) list.push('general');
        (structure.base_ppe || []).forEach(function (k) { ppe[k] = 1; });
        list.forEach(function (w) {
            (structure.works[w] || []).forEach(function (h) {
                if (seen[h]) return;
                seen[h] = 1;
                var hz = structure.hazards[h];
                if (!hz) return;
                var controls = [];
                Object.keys(hz.controls).forEach(function (cid) {
                    controls.push({ lvl: hz.controls[cid], t: (lib.control || {})[cid] || cid, k: cid });
                });
                rows.push({
                    id: uid(), src: 'lib', wk: w, hk: h,
                    step: (lib.work || {})[w] || w,
                    hazard: (lib.hazard || {})[h] || h,
                    who: (hz.who || []).slice(),
                    l: hz.l, s: hz.s, l2: hz.l2, s2: hz.s2,
                    controls: controls, resp: '', due: '',
                });
                (hz.ppe || []).forEach(function (k) { ppe[k] = 1; });
                (hz.permits || []).forEach(function (k) { permits[k] = 1; });
            });
        });
        return { rows: rows, ppe: Object.keys(ppe), permits: Object.keys(permits) };
    }

    /**
     * Строки, собранные из библиотеки и не правленные человеком, переводятся
     * на другой язык сайта даром — их тексты есть в словаре по ключам.
     */
    function retext(row, vocab) {
        var lib = (vocab && vocab.lib) || {};
        var out = JSON.parse(JSON.stringify(row));
        if (out.hk && (lib.hazard || {})[out.hk]) out.hazard = lib.hazard[out.hk];
        if (out.wk && out.stepLib !== false && (lib.work || {})[out.wk]) out.step = lib.work[out.wk];
        (out.controls || []).forEach(function (c) {
            if (c.k && (lib.control || {})[c.k]) c.t = lib.control[c.k];
        });
        return out;
    }

    function rowIsLibrary(r) {
        if (!r.hk) return false;
        for (var i = 0; i < (r.controls || []).length; i++) if (!r.controls[i].k) return false;
        return true;
    }

    /**
     * Пункты листа инструктажа на языке бригады. Порядок источников:
     *   1. перевод модели для ЭТОГО текста строки (отпечаток совпал);
     *   2. строка из библиотеки без правок, язык бригады — язык сайта;
     *   3. текст документа как есть, с пометкой «не переведено» (ok:false).
     */
    function crewItems(state, crewVocab) {
        var ai = {};
        if (state.crew && state.crew.lang === state.crewLang && state.crew.items) {
            state.crew.items.forEach(function (it) { if (it.h) ai[it.h] = it; });
        }
        var same = state.crewLang === state.docLang;
        return (state.rows || []).map(function (r) {
            var h = rowHash(r);
            var doText = (r.controls || []).map(function (c) { return c.t; }).join(' ');
            if (same) return { hazard: r.hazard, do: doText, ok: true };
            if (ai[h]) return { hazard: ai[h].hazard, do: ai[h].do, ok: true };
            if (crewVocab && rowIsLibrary(r)) {
                var t = retext(r, crewVocab);
                return { hazard: t.hazard, do: t.controls.map(function (c) { return c.t; }).join(' '), ok: true };
            }
            return { hazard: r.hazard, do: doText, ok: false };
        });
    }

    function lines(v) {
        if (Array.isArray(v)) return v.filter(function (x) { return String(x || '').trim() !== ''; });
        return String(v || '').split(/\r?\n/).map(function (x) { return x.trim(); }).filter(Boolean);
    }

    /* Подпись графы: родная у формата, если документ на языке формата. */
    function colLabel(fmt, key, docLang, vocab) {
        if (fmt.lang === docLang && fmt.labels && fmt.labels['col.' + key]) return fmt.labels['col.' + key];
        return ((((vocab || {}).doc || {}).col) || {})[key] || key;
    }
    function secLabel(fmt, key, docLang, vocab) {
        if (fmt.lang === docLang && fmt.labels && fmt.labels['sec.' + key]) return fmt.labels['sec.' + key];
        return ((((vocab || {}).doc || {}).sec) || {})[key] || key;
    }

    function ppeLines(state, vocab) {
        var lib = (vocab && vocab.lib) || {};
        var out = (state.ppe || []).map(function (k) { return (lib.ppe || {})[k] || k; });
        return out.concat(lines(state.ppeOther));
    }
    function permitLines(state, vocab) {
        var lib = (vocab && vocab.lib) || {};
        var out = (state.permits || []).map(function (k) { return (lib.permit || {})[k] || k; });
        return out.concat(lines(state.permitsOther));
    }

    /**
     * Печатный документ — одна вёрстка на PDF и «Печать». Возвращает HTML-строку
     * (всё экранировано); страница кладёт её в узел и отдаёт __generatePDF.
     *
     * @param {object} p {state, fmt, docVocab, crewVocab, crewLabels, ui}
     */
    function printable(p) {
        var st = p.state, fmt = p.fmt, dv = p.docVocab || {}, d = dv.doc || {};
        var cv = p.crewVocab || null, crew = (cv && cv.crew) || {}, cl = p.crewLabels || {};
        var docLang = st.docLang, crewLang = st.crewLang;
        var dDir = RTL[docLang] ? 'rtl' : 'ltr', cDir = RTL[crewLang] ? 'rtl' : 'ltr';
        var cols = fmt.columns || [];
        var rated = fmt.matrix !== 'none';
        var h = [];
        // Направление отчёта — языка документа (страница ставит его на узел).
        var E = function (s) { return wordSpans(s, dDir, dDir === 'rtl'); };

        function list(arr) {
            if (!arr.length) return '<p class="rap-empty">—</p>';
            return '<ul class="rap-ul rap-inline">' + arr.map(function (x) { return '<li>' + E(x) + '</li>'; }).join('') + '</ul>';
        }
        function riskCell(l, s) {
            var r = rate(fmt.matrix, l, s, docLang, dv);
            return '<td class="rap-risk rap-' + esc(r.band) + '">' + E(r.text) + '</td>';
        }
        function who(r) {
            var w = (d.who || {});
            return (r.who || []).map(function (k) { return w[k] || k; }).join('; ');
        }

        h.push('<section class="rap-doc" lang="' + esc(docLang) + '" dir="' + dDir + '">');
        h.push('<div class="rap-draft">' + E(d.draft_mark || '') + '</div>');
        h.push('<h1 class="rap-title">' + E(fmt.title) + '</h1>');
        if (st.title) h.push('<p class="rap-sub">' + E(st.title) + '</p>');

        var fields = [
            [d.f_company, st.company], [d.f_site, st.site], [d.f_job, st.job],
            [d.f_date, st.date], [d.f_prepared, st.prepared], [d.f_review, ''],
        ];
        h.push('<table class="rap-fields"><colgroup><col style="width:14%"><col style="width:36%"><col style="width:14%"><col style="width:36%"></colgroup><tbody>');
        for (var i = 0; i < fields.length; i += 2) {
            h.push('<tr><th>' + E(fields[i][0]) + '</th><td>' + E(fields[i][1] || '') + '</td>'
                + '<th>' + E(fields[i + 1][0]) + '</th><td>' + E(fields[i + 1][1] || '') + '</td></tr>');
        }
        h.push('</tbody></table>');

        if ((fmt.sections || []).indexOf('hrcw') >= 0 && fmt.hrcw && fmt.hrcw.length) {
            h.push('<h2>' + E(secLabel(fmt, 'hrcw', docLang, dv)) + '</h2><ul class="rap-check">');
            fmt.hrcw.forEach(function (t, k) {
                var on = (st.hrcw || []).indexOf(k) >= 0;
                h.push('<li><span class="rap-box">' + (on ? '✓' : '') + '</span>' + E(t) + '</li>');
            });
            h.push('</ul>');
        }

        var method = lines(st.method);
        if ((fmt.sections || []).indexOf('method') >= 0 && method.length) {
            h.push('<h2>' + E(secLabel(fmt, 'method', docLang, dv)) + '</h2><ol class="rap-ol">');
            method.forEach(function (m) { h.push('<li>' + E(m) + '</li>'); });
            h.push('</ol>');
        }

        /* Шапка таблицы и первая строка — ОДНА неразрывная таблица, остальные
           строки — вторая с теми же ширинами граф. Иначе PDF-конвейер оставлял
           шапку одну внизу первого листа, а строки уходили на второй (замер на
           SWMS с перечнем работ повышенного риска, 21.10.2026). */
        var shown = cols.filter(function (c) { return rated || (c !== 'risk_before' && c !== 'risk_after'); });
        var wsum = 0;
        shown.forEach(function (c) { wsum += COL_WEIGHT[c] || 1; });
        var colgroup = '<colgroup><col style="width:3%">' + shown.map(function (c) {
            return '<col style="width:' + (97 * (COL_WEIGHT[c] || 1) / wsum).toFixed(2) + '%">';
        }).join('') + '</colgroup>';
        var head = '<thead><tr><th class="rap-n">#</th>' + shown.map(function (c) {
            return '<th class="rap-c-' + c + '">' + E(colLabel(fmt, c, docLang, dv)) + '</th>';
        }).join('') + '</tr></thead>';
        h.push('<table class="rap-grid rap-avoid">' + colgroup + head + '<tbody>');
        (st.rows || []).forEach(function (r, n) {
            if (n === 1) h.push('</tbody></table><table class="rap-grid rap-cont">' + colgroup + '<tbody>');
            h.push('<tr><td class="rap-n">' + (n + 1) + '</td>');
            cols.forEach(function (c) {
                if (c === 'step') h.push('<td>' + E(r.step) + '</td>');
                else if (c === 'hazard') h.push('<td>' + E(r.hazard) + '</td>');
                else if (c === 'who') h.push('<td>' + E(who(r)) + '</td>');
                else if (c === 'risk_before') { if (rated) h.push(riskCell(r.l, r.s)); }
                else if (c === 'risk_after') { if (rated) h.push(riskCell(r.l2, r.s2)); }
                else if (c === 'controls') {
                    h.push('<td><ul class="rap-ctl">' + (r.controls || []).map(function (ct) {
                        return '<li><b class="rap-lvl rap-lvl-' + esc(ct.lvl) + '">' + E(((d.lvl || {})[ct.lvl]) || ct.lvl) + '</b> ' + E(ct.t) + '</li>';
                    }).join('') + '</ul></td>');
                }
                else if (c === 'responsible') h.push('<td>' + E(r.resp) + '</td>');
                else if (c === 'due') h.push('<td>' + E(r.due) + '</td>');
            });
            h.push('</tr>');
        });
        h.push('</tbody></table>');
        if (rated) h.push('<p class="rap-legend"><b>' + E((d.sec || {}).legend || '') + ':</b> ' + E(legend(fmt.matrix, docLang, dv)) + '</p>');
        h.push('<p class="rap-legend">' + LEVELS.map(function (k) {
            return '<b class="rap-lvl rap-lvl-' + k + '">' + E((d.lvl || {})[k] || k) + '</b>';
        }).join(' › ') + '</p>');

        var blocks = [
            ['ppe', ppeLines(st, dv)], ['permits', permitLines(st, dv)],
            ['competency', lines(st.competency)], ['plant', lines(st.plant)],
        ];
        h.push('<div class="rap-cols">');
        blocks.forEach(function (b) {
            if ((fmt.sections || []).indexOf(b[0]) < 0) return;
            h.push('<div class="rap-avoid"><h2>' + E(secLabel(fmt, b[0], docLang, dv)) + '</h2>' + list(b[1]) + '</div>');
        });
        h.push('</div>');
        if ((fmt.sections || []).indexOf('emergency') >= 0) {
            h.push('<div class="rap-avoid"><h2>' + E(secLabel(fmt, 'emergency', docLang, dv)) + '</h2><p class="rap-pre">' + E(st.emergency || '') + '</p></div>');
        }
        if ((fmt.sections || []).indexOf('monitor') >= 0) {
            h.push('<div class="rap-avoid"><h2>' + E(secLabel(fmt, 'monitor', docLang, dv)) + '</h2><p class="rap-pre">' + E(st.monitor || '') + '</p></div>');
        }
        h.push('<div class="rap-avoid"><table class="rap-fields rap-approve"><tbody><tr><th>' + E(d.f_approved) + '</th><td>' + E(st.approved || '') + '</td><th>'
            + E(d.f_signature) + '</th><td></td><th>' + E(d.f_date) + '</th><td></td></tr></tbody></table>');
        if ((fmt.sources || []).length) {
            h.push('<p class="rap-src"><b>' + E((d.sec || {}).sources || '') + ':</b> ' + fmt.sources.map(function (s) { return E(s.title); }).join('; ') + '</p>');
        }
        if (p.disclaimer) h.push('<p class="rap-src">' + E(p.disclaimer) + '</p>');
        h.push('</div></section>');

        // ── лист инструктажа: на языке бригады ──
        var items = crewItems(st, cv);
        var L = function (k, fb) { return cl[k] || crew[k] || fb || ''; };
        var dCrew = d && dv.crew ? dv.crew : {};
        h.push('<section class="rap-crew rap-pb" lang="' + esc(crewLang) + '" dir="' + cDir + '">');
        h.push('<h1 class="rap-title">' + E(L('title', dCrew.title)) + '</h1>');
        h.push('<p class="rap-sub">' + E(L('intro', dCrew.intro)) + '</p>');
        h.push('<table class="rap-grid"><thead><tr><th class="rap-n">#</th><th>' + E(L('hazard', dCrew.hazard)) + '</th><th>'
            + E(L('do', dCrew.do)) + '</th></tr></thead><tbody>');
        items.forEach(function (it, n) {
            h.push('<tr><td class="rap-n">' + (n + 1) + '</td><td>' + (it.ok ? '' : '⚠ ') + E(it.hazard) + '</td><td>' + E(it.do) + '</td></tr>');
        });
        h.push('</tbody></table>');
        var crewPpe = (st.crew && st.crew.lang === crewLang && st.crew.ppe) ? st.crew.ppe
            : (cv ? ppeLines(st, cv).join(', ') : ppeLines(st, dv).join(', '));
        h.push('<p><b>' + E(L('ppe', dCrew.ppe)) + ':</b> ' + E(crewPpe) + '</p>');
        h.push('<p class="rap-stop">' + E(L('stop', dCrew.stop)) + '</p>');
        if (st.emergency) h.push('<p><b>' + E(L('emergency', dCrew.emergency)) + ':</b></p><p class="rap-pre">' + E(st.emergency) + '</p>');
        h.push('</section>');

        // ── лист подписей: утверждение на обоих языках ──
        h.push('<section class="rap-sign rap-pb" lang="' + esc(docLang) + '" dir="' + dDir + '">');
        h.push('<h1 class="rap-title">' + E(secLabel(fmt, 'signoff', docLang, dv)) + '</h1>');
        h.push('<p>' + E(d.signoff_statement || '') + '</p>');
        var crewStatement = cl.signoff_statement || ((cv && cv.doc) ? cv.doc.signoff_statement : '');
        if (crewLang !== docLang && crewStatement) {
            h.push('<p lang="' + esc(crewLang) + '" dir="' + cDir + '">' + E(crewStatement) + '</p>');
        }
        var two = function (k, crewKey) {
            var a = d[k] || '';
            var b = crewLang !== docLang ? (cl[crewKey] || ((cv && cv.doc) ? cv.doc[k] : '')) : '';
            return E(a) + (b && b !== a ? '<br><span class="rap-alt">' + E(b) + '</span>' : '');
        };
        h.push('<table class="rap-grid rap-signs"><thead><tr><th class="rap-n">#</th><th>' + two('signoff_name', 'name') + '</th><th>'
            + two('signoff_role', 'role') + '</th><th>' + two('signoff_sign', 'sign') + '</th><th>' + two('signoff_date', 'date') + '</th></tr></thead><tbody>');
        for (var k = 1; k <= 14; k++) h.push('<tr><td class="rap-n">' + k + '</td><td></td><td></td><td></td><td></td></tr>');
        h.push('</tbody></table></section>');
        return h.join('');
    }

    /**
     * Тот же документ блоками DocxWriter (Word). A4 книжный: ширины граф в
     * твипах делят 9 922 (ширина набора между полями).
     */
    function docxBlocks(p) {
        var st = p.state, fmt = p.fmt, dv = p.docVocab || {}, d = dv.doc || {};
        var docLang = st.docLang, rated = fmt.matrix !== 'none';
        var right = RTL[docLang] ? 'right' : 'left';
        var dDir = RTL[docLang] ? 'rtl' : 'ltr';
        // Абзац и ячейка — направление своего текста (английская мера в
        // арабском документе — ltr, арабский абзац — rtl), таблица — языка.
        // Текст без единой буквы (дата, номер) — ltr: в rtl-абзаце Word
        // показывал 2026-10-22 как «22-10-2026».
        var withDir = function (o) { o.dir = textDir(o.text != null ? o.text : (o.runs || []).map(function (r) { return typeof r === 'string' ? r : r.text; }).join(' '), 'ltr'); return o; };
        var dirCells = function (row) {
            return row.map(function (c) {
                var o = c && typeof c === 'object' ? Object.assign({}, c) : { text: c };
                o.dir = textDir(o.text, 'ltr');
                return o;
            });
        };
        var b = [];
        var push = function (o) { b.push(withDir(o)); };
        push({ text: d.draft_mark || '', bold: true, size: 18, color: 'B45309', align: right });
        push({ text: fmt.title, bold: true, size: 32, spaceBefore: 120, spaceAfter: 60, align: right });
        if (st.title) push({ text: st.title, size: 22, spaceAfter: 120, align: right });
        b.push({ table: { widths: [2000, 2961, 2000, 2961], size: 18, rtl: dDir === 'rtl', align: [right, right, right, right], rows: [
            [{ text: d.f_company, bold: true }, st.company || '', { text: d.f_site, bold: true }, st.site || ''],
            [{ text: d.f_job, bold: true }, st.job || '', { text: d.f_date, bold: true }, st.date || ''],
            [{ text: d.f_prepared, bold: true }, st.prepared || '', { text: d.f_approved, bold: true }, st.approved || ''],
        ].map(function (row) { return dirCells(row); }) } });

        if ((fmt.sections || []).indexOf('hrcw') >= 0 && fmt.hrcw && fmt.hrcw.length) {
            push({ text: secLabel(fmt, 'hrcw', docLang, dv), bold: true, size: 24, spaceBefore: 200, spaceAfter: 80, align: right });
            fmt.hrcw.forEach(function (t, k) {
                push({ text: ((st.hrcw || []).indexOf(k) >= 0 ? '☒ ' : '☐ ') + t, size: 20, align: right });
            });
        }
        var method = lines(st.method);
        if ((fmt.sections || []).indexOf('method') >= 0 && method.length) {
            push({ text: secLabel(fmt, 'method', docLang, dv), bold: true, size: 24, spaceBefore: 200, spaceAfter: 80, align: right });
            method.forEach(function (m, k) { push({ text: (k + 1) + '. ' + m, size: 20, align: right }); });
        }

        var cols = (fmt.columns || []).filter(function (c) { return rated || (c !== 'risk_before' && c !== 'risk_after'); });
        var weight = COL_WEIGHT;
        var total = 0;
        cols.forEach(function (c) { total += weight[c] || 1; });
        var avail = 9922 - 420;
        var widths = [420].concat(cols.map(function (c) { return Math.round(avail * (weight[c] || 1) / total); }));
        var rows = (st.rows || []).map(function (r, n) {
            var cells = [String(n + 1)];
            cols.forEach(function (c) {
                if (c === 'step') cells.push(r.step || '');
                else if (c === 'hazard') cells.push(r.hazard || '');
                else if (c === 'who') cells.push((r.who || []).map(function (k) { return (d.who || {})[k] || k; }).join('; '));
                else if (c === 'risk_before') cells.push(rate(fmt.matrix, r.l, r.s, docLang, dv).text);
                else if (c === 'risk_after') cells.push(rate(fmt.matrix, r.l2, r.s2, docLang, dv).text);
                // Меры — строки ОДНОГО абзаца с меткой уровня на языке документа:
                // мера другого направления в нём переламывалась на переносе
                // (Word: «.check it daily ,361»), поэтому — с метками своего направления.
                else if (c === 'controls') cells.push((r.controls || []).map(function (ct) { return '[' + (((d.lvl || {})[ct.lvl]) || ct.lvl) + '] ' + marked(ct.t, dDir); }).join('\n'));
                else if (c === 'responsible') cells.push(r.resp || '');
                else if (c === 'due') cells.push(r.due || '');
            });
            return cells;
        });
        b.push({ text: '', spaceBefore: 120 });
        b.push({ table: {
            widths: widths, size: 16, rtl: dDir === 'rtl',
            head: dirCells(['#'].concat(cols.map(function (c) { return colLabel(fmt, c, docLang, dv); }))),
            rows: rows.map(function (row) { return dirCells(row); }), align: widths.map(function () { return right; }),
        } });
        if (rated) push({ text: ((d.sec || {}).legend || '') + ': ' + legend(fmt.matrix, docLang, dv), size: 16, spaceBefore: 80, align: right });
        push({ text: LEVELS.map(function (k) { return (d.lvl || {})[k] || k; }).join(' › '), size: 16, align: right });

        [['ppe', ppeLines(st, dv)], ['permits', permitLines(st, dv)], ['competency', lines(st.competency)], ['plant', lines(st.plant)]].forEach(function (s) {
            if ((fmt.sections || []).indexOf(s[0]) < 0) return;
            push({ text: secLabel(fmt, s[0], docLang, dv), bold: true, size: 24, spaceBefore: 200, spaceAfter: 60, align: right });
            (s[1].length ? s[1] : ['—']).forEach(function (x) { push({ text: '• ' + x, size: 20, align: right }); });
        });
        ['emergency', 'monitor'].forEach(function (k) {
            if ((fmt.sections || []).indexOf(k) < 0) return;
            push({ text: secLabel(fmt, k, docLang, dv), bold: true, size: 24, spaceBefore: 200, spaceAfter: 60, align: right });
            push({ text: st[k] || '', size: 20, align: right });
        });
        if ((fmt.sources || []).length) {
            push({ text: ((d.sec || {}).sources || '') + ': ' + fmt.sources.map(function (s) { return s.title; }).join('; '), size: 16, spaceBefore: 200, align: right });
        }
        if (p.disclaimer) push({ text: p.disclaimer, size: 16, align: right });

        // Лист инструктажа — на языке бригады, как в PDF.
        var cv = p.crewVocab || null, crew = (cv && cv.crew) || {}, cl = p.crewLabels || {}, dCrew = dv.crew || {};
        var crewRight = RTL[st.crewLang] ? 'right' : 'left', cDir = RTL[st.crewLang] ? 'rtl' : 'ltr';
        var L = function (k) { return cl[k] || crew[k] || dCrew[k] || ''; };
        var items = crewItems(st, cv);
        b.push({ pageBreak: true });
        push({ text: L('title'), bold: true, size: 28, spaceAfter: 60, align: crewRight });
        push({ text: L('intro'), size: 20, spaceAfter: 120, align: crewRight });
        b.push({ table: { widths: [420, 3700, 5802], size: 18, rtl: cDir === 'rtl', align: ['left', crewRight, crewRight],
            head: dirCells(['#', L('hazard'), L('do')]),
            rows: items.map(function (it, n) { return dirCells([String(n + 1), (it.ok ? '' : '⚠ ') + it.hazard, it.do]); }) } });
        var crewPpe = (st.crew && st.crew.lang === st.crewLang && st.crew.ppe) ? st.crew.ppe : ppeLines(st, cv || dv).join(', ');
        push({ runs: [{ text: L('ppe') + ': ', bold: true }, crewPpe], size: 20, spaceBefore: 120, align: crewRight });
        push({ text: L('stop'), bold: true, size: 20, spaceBefore: 80, align: crewRight });

        // Лист подписей: утверждение на языке документа и, если он другой, бригады.
        b.push({ pageBreak: true });
        push({ text: secLabel(fmt, 'signoff', docLang, dv), bold: true, size: 28, spaceAfter: 80, align: right });
        push({ text: d.signoff_statement || '', size: 20, spaceAfter: 60, align: right });
        var crewStatement = cl.signoff_statement || ((cv && cv.doc) ? cv.doc.signoff_statement : '');
        if (st.crewLang !== docLang && crewStatement) push({ text: crewStatement, size: 20, spaceAfter: 120, align: crewRight });
        var both = function (k, ck) {
            var a = d[k] || '', c = st.crewLang !== docLang ? (cl[ck] || ((cv && cv.doc) ? cv.doc[k] : '')) : '';
            return c && c !== a ? a + '\n' + c : a;
        };
        var signRows = [];
        for (var k = 1; k <= 14; k++) signRows.push([String(k), '', '', '', '']);
        b.push({ table: { widths: [420, 3000, 2502, 2500, 1500], size: 20, rtl: dDir === 'rtl',
            head: dirCells(['#', both('signoff_name', 'name'), both('signoff_role', 'role'), both('signoff_sign', 'sign'), both('signoff_date', 'date')]), rows: signRows } });
        return b;
    }

    var api = {
        LEVELS: LEVELS, RTL: RTL,
        esc: esc, scale: scale, snap: snap, rate: rate, legend: legend,
        buildFromLibrary: buildFromLibrary, retext: retext, rowIsLibrary: rowIsLibrary,
        rowHash: rowHash, crewItems: crewItems, lines: lines,
        colLabel: colLabel, secLabel: secLabel, printable: printable, docxBlocks: docxBlocks, uid: uid,
        textDir: textDir, marked: marked, wordSpans: wordSpans,
    };
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
    if (root) root.RACore = api;
})(typeof window !== 'undefined' ? window : (typeof globalThis !== 'undefined' ? globalThis : null));
